import type { TextToSpeechPort, TextToSpeechRequest, TextToSpeechResult } from '../../../ports/TextToSpeechPort';
import { gaiaHttpUrl } from '../url/GaiaUrls';

type GaiaTextToSpeechResponse = {
  audio_base64?: unknown;
  audioBase64?: unknown;
  content_type?: unknown;
  contentType?: unknown;
  sample_rate?: unknown;
  sampleRate?: unknown;
  provider?: unknown;
  duration_ms?: unknown;
  durationMs?: unknown;
};

export class GaiaTextToSpeechHttp implements TextToSpeechPort {
  constructor(private baseUrl: string) {}

  async synthesize(request: TextToSpeechRequest): Promise<TextToSpeechResult> {
    const text = request.text.trim();
    if (!text) throw new Error('Cannot synthesize empty text');

    const body: Record<string, unknown> = { text };
    if (request.characterId) body.character_id = request.characterId;
    if (request.outputFormat) body.output_format = request.outputFormat;

    const res = await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/speech/synthesis'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${request.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Gaia synthesis failed: ${res.status}`);

    return normalizeTextToSpeechResponse(await res.json());
  }
}

function normalizeTextToSpeechResponse(data: GaiaTextToSpeechResponse): TextToSpeechResult {
  const audioBase64 = stringField(data.audio_base64) ?? stringField(data.audioBase64);
  if (!audioBase64) throw new Error('Gaia synthesis response missing audio');

  const contentType = stringField(data.content_type) ?? stringField(data.contentType) ?? 'audio/mpeg';
  return {
    audio: base64ToBlob(audioBase64, contentType),
    contentType,
    sampleRate: numberField(data.sample_rate) ?? numberField(data.sampleRate),
    provider: stringField(data.provider),
    durationMs: numberField(data.duration_ms) ?? numberField(data.durationMs),
  };
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

function stringField(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function numberField(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
